// Message search highlights (`SearchMessageHit.highlights`): fragments of a message's raw content
// with every match wrapped in U+E000 … U+E001 — private-use characters that never occur in user
// text, so they can't collide with markdown or HTML in the message. Turned into safe HTML here:
// everything is escaped first, then only the markers become <mark>.
// Mention tokens (`<@id>` / `<#id>`, see mentionTokens.ts) are turned into readable names before
// that, and halves of tokens a fragment was cut through are dropped.

import { decodeMentions, stripPartialMentionTokens, type MentionLookups } from '~/lib/chat/mentionTokens'

const MATCH_START = ''
const MATCH_END = ''

function escapeHtml(text: string): string {
  return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
}

/** One fragment → HTML with `<mark>` around the matches; unbalanced markers are dropped. */
export function renderHighlightFragment(fragment: string): string {
  let html = ''
  let open = false
  for (const part of fragment.split(/([])/)) {
    if (part === MATCH_START) {
      if (!open) html += '<mark data-search-match="">'
      open = true
    } else if (part === MATCH_END) {
      if (open) html += '</mark>'
      open = false
    } else {
      html += escapeHtml(part)
    }
  }
  if (open) html += '</mark>'
  return html
}

/** Plain text of a fragment, markers removed — for titles / aria labels. */
export function stripHighlightMarkers(fragment: string): string {
  return fragment.replace(/[]/g, '')
}

/** Content shown for a hit: its highlight fragments joined with «…», or the start of the message. */
export function hitPreviewHtml(highlights: string[], content: string, maxLength = 300, lookups?: MentionLookups): string {
  if (highlights.length) {
    return highlights
        .map((fragment) => renderHighlightFragment(stripPartialMentionTokens(decodeMentions(fragment, lookups))))
        .join(' <span class="text-muted-foreground">…</span> ')
  }
  const readable = decodeMentions(content, lookups)
  const text = readable.length > maxLength ? `${readable.slice(0, maxLength)}…` : readable
  return escapeHtml(text)
}
